import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { GoogleAddressInput } from '@components/atoms/GoogleAddressInput';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import {
  Geofence,
  GeofenceCreateInput,
  GeofenceShapeType,
  GeofenceUpdateInput,
  LatLng,
} from '@models/geofence.model';
import { useActiveGeofenceCategories } from '@hooks/api/geofence';
import { useProjects } from '@hooks/api/projects/useProjects';
import { useWarehouses } from '@hooks/api/warehouse/useWarehouses';
import { DEFAULT_GEOFENCE_COLOR } from '@utils/geofenceCategoryIcons';
import GeofenceDrawingMap, {
  DrawnShapeValue,
} from '@components/organisms/GeofenceDrawingMap';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RoomIcon from '@mui/icons-material/Room';
import PolylineIcon from '@mui/icons-material/Polyline';
import MapIcon from '@mui/icons-material/Map';

interface GeofenceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: GeofenceCreateInput | GeofenceUpdateInput) => Promise<void>;
  geofence?: Geofence | null;
  isLoading?: boolean;
  /** Pre-fill projectId or warehouseId when opening from a context page */
  defaultProjectId?: string | null;
  defaultWarehouseId?: string | null;
}

const MIN_RADIUS = 10;
const MAX_RADIUS = 50000;
const DEFAULT_RADIUS = 100;

export const GeofenceFormModal: React.FC<GeofenceFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  geofence,
  isLoading = false,
  defaultProjectId = null,
  defaultWarehouseId = null,
}) => {
  const isEditing = !!geofence;

  const { data: categories = [] } = useActiveGeofenceCategories();
  const { data: projects = [] } = useProjects();
  const { data: warehouses = [] } = useWarehouses();

  // ========== Form state ==========
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  // Lazy init from the geofence prop so the very first render already has the
  // correct shape type. Without this, `initialDrawnValue` would compute as
  // undefined on first render (because shapeType !== geofence.shape.type) and
  // the drawing map's `showInitial` state would lock to false.
  const [shapeType, setShapeType] = useState<GeofenceShapeType>(
    () => geofence?.shape.type ?? GeofenceShapeType.POINT_RADIUS,
  );
  const [drawnShape, setDrawnShape] = useState<DrawnShapeValue | null>(null);
  const [radiusMeters, setRadiusMeters] = useState<string>(`${DEFAULT_RADIUS}`);
  const [projectId, setProjectId] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string>('');
  // Map control state — address search + reactive pan when project/warehouse changes
  const [searchAddress, setSearchAddress] = useState<string>('');
  const [panToCoords, setPanToCoords] = useState<LatLng | null>(null);

  // Selected category (used to color the drawing map preview)
  const selectedCategory = useMemo(
    () => categories.find((c) => c.geofenceCategoryId === categoryId) ?? null,
    [categories, categoryId],
  );
  const drawColor = selectedCategory?.color || DEFAULT_GEOFENCE_COLOR;

  const initialDrawnValue = useMemo<DrawnShapeValue | undefined>(() => {
    if (!geofence?.shape) return undefined;
    // If the user switched the shape type while editing, drop the initial value
    // so the drawing map starts blank and the user can draw the new shape.
    if (shapeType !== geofence.shape.type) return undefined;
    if (geofence.shape.type === GeofenceShapeType.POINT_RADIUS) {
      if (!geofence.shape.center || !geofence.shape.radiusMeters) return undefined;
      return {
        shapeType: GeofenceShapeType.POINT_RADIUS,
        centerPoint: geofence.shape.center,
        radiusMeters: geofence.shape.radiusMeters,
      };
    }
    if (
      geofence.shape.type === GeofenceShapeType.POLYGON &&
      geofence.shape.polygonCoordinates &&
      geofence.shape.polygonCoordinates.length >= 3
    ) {
      return {
        shapeType: GeofenceShapeType.POLYGON,
        polygonCoordinates: geofence.shape.polygonCoordinates,
      };
    }
    return undefined;
  }, [geofence, shapeType]);

  // Suggested center: project/warehouse coords if linked
  const suggestedCenter = useMemo<LatLng | undefined>(() => {
    if (projectId) {
      const p = projects.find((x: any) => x.projectId === projectId);
      if (p?.lat && p?.long) return { lat: p.lat, lng: p.long };
    }
    if (warehouseId) {
      const w = warehouses.find((x: any) => x.warehouseId === warehouseId);
      if (w?.lat && w?.long) return { lat: w.lat, lng: w.long };
    }
    return undefined;
  }, [projectId, warehouseId, projects, warehouses]);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    if (geofence) {
      setName(geofence.name || '');
      setDescription(geofence.description || '');
      setCategoryId(geofence.geofenceCategoryId || '');
      setShapeType(geofence.shape.type);
      setRadiusMeters(
        geofence.shape.radiusMeters
          ? `${geofence.shape.radiusMeters}`
          : `${DEFAULT_RADIUS}`,
      );
      setProjectId(geofence.projectId || '');
      setWarehouseId(geofence.warehouseId || '');
      setIsActive(geofence.isActive ?? true);
      setDrawnShape(
        geofence.shape.type === GeofenceShapeType.POINT_RADIUS
          ? geofence.shape.center && geofence.shape.radiusMeters
            ? {
                shapeType: GeofenceShapeType.POINT_RADIUS,
                centerPoint: geofence.shape.center,
                radiusMeters: geofence.shape.radiusMeters,
              }
            : null
          : geofence.shape.polygonCoordinates &&
            geofence.shape.polygonCoordinates.length >= 3
          ? {
              shapeType: GeofenceShapeType.POLYGON,
              polygonCoordinates: geofence.shape.polygonCoordinates,
            }
          : null,
      );
    } else {
      setName('');
      setDescription('');
      setCategoryId('');
      setShapeType(GeofenceShapeType.POINT_RADIUS);
      setRadiusMeters(`${DEFAULT_RADIUS}`);
      setProjectId(defaultProjectId || '');
      setWarehouseId(defaultWarehouseId || '');
      setIsActive(true);
      setDrawnShape(null);
    }
    setError('');
    setSearchAddress('');
    setPanToCoords(null);
  }, [isOpen, geofence, defaultProjectId, defaultWarehouseId]);

  // When the modal opens with a default project/warehouse pre-selected, pan the map there.
  useEffect(() => {
    if (!isOpen || geofence) return;
    if (defaultProjectId) {
      const p: any = projects.find((x: any) => x.projectId === defaultProjectId);
      if (p?.lat && p?.long) setPanToCoords({ lat: p.lat, lng: p.long });
    } else if (defaultWarehouseId) {
      const w: any = warehouses.find((x: any) => x.warehouseId === defaultWarehouseId);
      if (w?.lat && w?.long) setPanToCoords({ lat: w.lat, lng: w.long });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultProjectId, defaultWarehouseId, projects.length, warehouses.length]);

  // Sync radius input → drawn shape (only when point+radius and shape exists)
  useEffect(() => {
    if (
      drawnShape?.shapeType === GeofenceShapeType.POINT_RADIUS &&
      drawnShape.centerPoint
    ) {
      const parsed = parseInt(radiusMeters, 10);
      if (
        Number.isFinite(parsed) &&
        parsed !== drawnShape.radiusMeters &&
        parsed >= MIN_RADIUS &&
        parsed <= MAX_RADIUS
      ) {
        setDrawnShape({
          ...drawnShape,
          radiusMeters: parsed,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusMeters]);

  // When the user mutually selects project/warehouse, clear the other side and pan the map
  const handleProjectChange = (id: string) => {
    setProjectId(id);
    if (!id) return;
    setWarehouseId('');
    const p: any = projects.find((x: any) => x.projectId === id);
    if (p?.lat && p?.long) {
      setPanToCoords({ lat: p.lat, lng: p.long });
    }
  };
  const handleWarehouseChange = (id: string) => {
    setWarehouseId(id);
    if (!id) return;
    setProjectId('');
    const w: any = warehouses.find((x: any) => x.warehouseId === id);
    if (w?.lat && w?.long) {
      setPanToCoords({ lat: w.lat, lng: w.long });
    }
  };

  const handleAddressCoords = (lat?: number | null, lng?: number | null) => {
    if (typeof lat === 'number' && typeof lng === 'number') {
      setPanToCoords({ lat, lng });
    }
  };

  const categoryOptions: SelectOption[] = useMemo(
    () =>
      categories.map((c) => ({
        value: c.geofenceCategoryId,
        label: c.isGlobal ? `${c.name} (Global)` : c.name,
      })),
    [categories],
  );

  const projectOptions: SelectOption[] = useMemo(
    () =>
      [{ value: '', label: 'No project' } as SelectOption].concat(
        projects.map((p: any) => ({ value: p.projectId, label: p.name })),
      ),
    [projects],
  );

  const warehouseOptions: SelectOption[] = useMemo(
    () =>
      [{ value: '', label: 'No warehouse' } as SelectOption].concat(
        warehouses.map((w: any) => ({ value: w.warehouseId, label: w.name })),
      ),
    [warehouses],
  );

  const handleSubmit = async () => {
    setError('');
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!categoryId) {
      setError('Category is required.');
      return;
    }
    if (projectId && warehouseId) {
      setError('A geofence can be linked to a project OR a warehouse, not both.');
      return;
    }
    if (!drawnShape) {
      setError('Draw the geofence shape on the map before saving.');
      return;
    }
    if (
      shapeType === GeofenceShapeType.POINT_RADIUS &&
      (!drawnShape.centerPoint || !drawnShape.radiusMeters)
    ) {
      setError('Draw the circle on the map.');
      return;
    }
    if (shapeType === GeofenceShapeType.POINT_RADIUS) {
      const r = drawnShape.radiusMeters!;
      if (r < MIN_RADIUS || r > MAX_RADIUS) {
        setError(`Radius must be between ${MIN_RADIUS} and ${MAX_RADIUS} meters.`);
        return;
      }
    }
    if (
      shapeType === GeofenceShapeType.POLYGON &&
      (!drawnShape.polygonCoordinates || drawnShape.polygonCoordinates.length < 3)
    ) {
      setError('Polygon must have at least 3 vertices.');
      return;
    }

    if (isEditing && geofence) {
      const shapeTypeChanged = shapeType !== geofence.shape.type;
      const update: GeofenceUpdateInput = {
        geofenceId: geofence.geofenceId,
        name: name.trim(),
        description: description.trim() || undefined,
        geofenceCategoryId: categoryId,
        isActive,
        projectId: projectId || undefined,
        warehouseId: warehouseId || undefined,
      };
      // When the user switches shape type, the backend requires shapeType +
      // matching shape data so it can clear the previous shape's columns.
      if (shapeTypeChanged) {
        update.shapeType = shapeType;
      }
      if (shapeType === GeofenceShapeType.POINT_RADIUS) {
        update.centerPoint = drawnShape.centerPoint!;
        update.radiusMeters = drawnShape.radiusMeters!;
      } else {
        update.polygonCoordinates = drawnShape.polygonCoordinates!;
      }
      await onSubmit(update);
    } else {
      const create: GeofenceCreateInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        geofenceCategoryId: categoryId,
        shapeType,
        isActive,
        projectId: projectId || undefined,
        warehouseId: warehouseId || undefined,
      };
      if (shapeType === GeofenceShapeType.POINT_RADIUS) {
        create.centerPoint = drawnShape.centerPoint!;
        create.radiusMeters = drawnShape.radiusMeters!;
      } else {
        create.polygonCoordinates = drawnShape.polygonCoordinates!;
      }
      await onSubmit(create);
    }
  };

  const canConfirm = !!name.trim() && !!categoryId && !!drawnShape;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Geofence' : 'Add Geofence'}
      confirmText={isEditing ? 'Save Changes' : 'Create Geofence'}
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      disableConfirm={!canConfirm}
      size="large"
    >
      <div className="flex flex-col gap-5">
        {/* Details */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <InfoOutlinedIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Name *" htmlFor="geofenceName">
              <Input
                id="geofenceName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Downtown Office"
                autoFocus
                autoComplete="off"
                name="geofence-name"
              />
            </FormGroup>

            <FormGroup label="Category *" htmlFor="geofenceCategory">
              <ReactSelect
                options={categoryOptions}
                value={
                  categoryId
                    ? categoryOptions.find((o) => o.value === categoryId) || null
                    : null
                }
                onChange={(opt: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (opt && !Array.isArray(opt) && 'value' in opt) {
                    setCategoryId(opt.value as string);
                  }
                }}
                placeholder="Select a category..."
              />
            </FormGroup>

            <FormGroup label="Description" htmlFor="geofenceDescription">
              <textarea
                id="geofenceDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes..."
                rows={2}
                autoComplete="off"
                name="geofence-description"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              />
            </FormGroup>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={isActive}
                    onChange={() => setIsActive(true)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!isActive}
                    onChange={() => setIsActive(false)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Disabled</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Linked entity */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Linked entity (optional)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Project" htmlFor="geofenceProject">
              <ReactSelect
                options={projectOptions}
                value={projectOptions.find((o) => o.value === projectId) || projectOptions[0]}
                onChange={(opt: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (opt && !Array.isArray(opt) && 'value' in opt) {
                    handleProjectChange(opt.value as string);
                  }
                }}
                placeholder="Optional"
                isDisabled={!!warehouseId}
              />
            </FormGroup>

            <FormGroup label="Warehouse" htmlFor="geofenceWarehouse">
              <ReactSelect
                options={warehouseOptions}
                value={
                  warehouseOptions.find((o) => o.value === warehouseId) ||
                  warehouseOptions[0]
                }
                onChange={(opt: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (opt && !Array.isArray(opt) && 'value' in opt) {
                    handleWarehouseChange(opt.value as string);
                  }
                }}
                placeholder="Optional"
                isDisabled={!!projectId}
              />
            </FormGroup>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            A geofence can be linked to a Project OR a Warehouse, not both.
          </p>
        </div>

        {/* Shape */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Shape
          </h3>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              type="button"
              onClick={() => setShapeType(GeofenceShapeType.POINT_RADIUS)}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                shapeType === GeofenceShapeType.POINT_RADIUS
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              <RoomIcon sx={{ fontSize: 18 }} />
              Point + Radius
            </button>
            <button
              type="button"
              onClick={() => setShapeType(GeofenceShapeType.POLYGON)}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                shapeType === GeofenceShapeType.POLYGON
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              <PolylineIcon sx={{ fontSize: 18 }} />
              Polygon
            </button>
          </div>

          <div
            className={`grid grid-cols-1 ${
              shapeType === GeofenceShapeType.POINT_RADIUS ? 'sm:grid-cols-3' : ''
            } gap-3 mb-3`}
          >
            <div
              className={
                shapeType === GeofenceShapeType.POINT_RADIUS ? 'sm:col-span-2' : ''
              }
            >
              <FormGroup label="Search address" htmlFor="geofenceAddressSearch">
                <GoogleAddressInput
                  id="geofenceAddressSearch"
                  value={searchAddress}
                  onChange={setSearchAddress}
                  onChangeCoor={handleAddressCoords}
                  placeholder="Search a place to center the map..."
                />
              </FormGroup>
            </div>
            {shapeType === GeofenceShapeType.POINT_RADIUS && (
              <FormGroup
                label={`Radius (m) — ${MIN_RADIUS}–${MAX_RADIUS}`}
                htmlFor="geofenceRadius"
              >
                <Input
                  id="geofenceRadius"
                  type="number"
                  min={MIN_RADIUS}
                  max={MAX_RADIUS}
                  value={radiusMeters}
                  onChange={(e) => setRadiusMeters(e.target.value)}
                  autoComplete="off"
                  name="geofence-radius"
                />
              </FormGroup>
            )}
          </div>

          <GeofenceDrawingMap
            shapeType={shapeType}
            initialValue={isEditing ? initialDrawnValue : undefined}
            color={drawColor}
            suggestedCenter={suggestedCenter}
            panTo={panToCoords}
            onChange={(value) => {
              setDrawnShape(value);
              if (value?.radiusMeters) {
                setRadiusMeters(`${value.radiusMeters}`);
              }
            }}
          />

          {!drawnShape && (
            <p className="mt-2 text-xs text-gray-500">
              {shapeType === GeofenceShapeType.POINT_RADIUS
                ? 'Click and drag on the map to draw the circle.'
                : 'Click points on the map to draw the polygon. Close it by clicking the first vertex.'}
            </p>
          )}
        </div>

        {error && (
          <div className="px-4 py-3 rounded-md bg-red-50 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GeofenceFormModal;
